// src/app/modules/user/pages/patient/PatientDetailsPage.tsx
import React, {useState, useEffect} from 'react'
import {Navigate, Route, Routes, Outlet, useParams, useNavigate} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core'
import {ProfileDetailOverview} from '../../components/profile-detail/ProfileDetailOverview'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import axios from 'axios'
import {ProfileDetailHeader} from '../../components/profile-detail/ProfileDetailHeader'
import PatientForm from '../../components/forms/PatientForm'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {PatientTimeTracked} from '../../components/patient/PatientTimeTracked'
import PatientAlertsSettings from '../../components/patient/PatientAlertsSettings'
import PatientHealthCriticalityAlerts from '../../components/patient/PatientHealthCriticalityAlerts'
import PatientHealthMeasurement from '../../components/patient/PatientHealthMeasurement'
import PatientDevicesComponent from '../../components/patient/PatientDevicesComponent'
import PatientBillingReport from '../../components/patient/PatientBillingReport'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {
  FaStethoscope,
  FaClock,
  FaMobileAlt,
  FaUser,
  FaBell,
  FaCog,
  FaDollarSign,
} from 'react-icons/fa'
import RoutesRoleGuard from '../../components/permissions/RoutesPermissionGuard'
import MetaTags from '../../../../meta/MetaTags'
// Redux imports
import { useSelector, useDispatch } from 'react-redux'
import { selectCurrentPatient, selectPatientLoading, selectPatientError } from '../../../../../store/selectors'
import { fetchPatientDetails, updatePatientData, clearCurrentPatient } from '../../../../../store/slices/patientSlice'
import type { AppDispatch } from '../../../../../store/store'
import { useMADoctorDropdown } from '../../context/MADoctorDropdownContext'

interface LinkItem {
  title: string
  path: string
  label: string
  allowedRoles: number[]
  icon?: React.ReactNode
}

const PatientDetailsPage: React.FC = () => {
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const {patientId} = useParams<{patientId: string}>()
  const uuid = patientId ?? ''

  // 🔑 Refresh‐key for reloading child fetch‐effects
  const [refreshKey, setRefreshKey] = useState(0)

  // Use Redux state instead of local state
  const userData = useSelector(selectCurrentPatient)
  const loading = useSelector(selectPatientLoading)
  const error = useSelector(selectPatientError)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id

  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  // Fetch patient profile using Redux
  useEffect(() => {
    if (uuid) {
      // For MA, pass doctor_id as query parameter
      const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
      dispatch(fetchPatientDetails({ patientUuid: uuid, doctorId }))
    }
    
    // Cleanup when component unmounts or patient changes
    return () => {
      dispatch(clearCurrentPatient())
    }
  }, [uuid, dispatch, roleId, maSelectedDoctorId])

  // Handle errors
  useEffect(() => {
    if (error) {
      notifyError(error)
      navigate('/unauthorized')
    }
  }, [error, navigate])

  // Called by ProfileDetailHeader when manual‐entry form succeeds
  const handleManualEntrySuccess = () => {
    // bump key → remount PatientHealthMeasurement
    setRefreshKey((k) => k + 1)
  }

  const accountBreadCrumbs: PageLink[] = [
    {
      title: 'Patient Details',
      path: `patient-details/${uuid}`,
      isSeparator: false,
      isActive: false,
    },
    {title: '', path: '', isSeparator: true, isActive: false},
  ]

  const links: LinkItem[] = [
    {
      title: 'health metrics',
      path: `/patient-details/${uuid}/health-metrics`,
      label: 'health-metrics',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaStethoscope />,
    },
    {
      title: 'time tracked',
      path: `/patient-details/${uuid}/patient-time-tracked`,
      label: 'time-tracked',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaClock />,
    },
    {
      title: 'devices',
      path: `/patient-details/${uuid}/devices`,
      label: 'devices',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaMobileAlt />,
    },
    {
      title: 'overview',
      path: `/patient-details/${uuid}/overview`,
      label: 'overview',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaUser />,
    },
    {
      title: 'health alerts',
      path: `/patient-details/${uuid}/health-alert`,
      label: 'health-alert',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaBell />,
    },
    {
      title: 'alert settings',
      path: `/patient-details/${uuid}/alerts-settings`,
      label: 'alerts-settings',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaCog />,
    },
    {
      title: 'billing',
      path: `/patient-details/${uuid}/billing`,
      label: 'billing',
      allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaDollarSign />,
    },
  ]

  const handleEditPress = () => setIsModalOpen(!isModalOpen)

  const handleSubmit = async (values: any, resetForm: () => void) => {
  if (!userData?.id) return notifyError('Patient data not available');

  try {
    let payload: any;

    if (values.consent_form instanceof File) {
      const fd = new FormData()
      Object.entries(values).forEach(([k, v]) => {
        if (k === 'consent_form') return
        if (v !== null && v !== undefined) {
          fd.append(k, String(v))
        }
      })
      fd.append('consent_form', values.consent_form)
      payload = fd
    } else {
      const { consent_form, ...rest } = values
      payload = rest
    }

    const resp = await axios.post(
      API_ENDPOINTS.UPDATE_PATIENT(userData.id),
      payload,
      payload instanceof FormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {}
    );

    notifySuccess(resp.data.message);

    const updatedPatient = resp?.data?.data?.patient
    if (updatedPatient) {
      dispatch(updatePatientData(updatedPatient))
    } else {
      dispatch(
        updatePatientData({
          ...values,
          full_name: `${values.first_name} ${values.last_name}`,
        })
      )
    }
  } catch (err: any) {
    notifyError(err.response?.data?.message);
  } finally {
    setIsModalOpen(false);
    resetForm();
  }
};

  const fullName = userData
    ? [userData.first_name, userData.last_name].filter(Boolean).join(' ')
    : ''

  return (
    <>
      <MetaTags title={fullName} />
      {/* <BlurSection
        isSubscribed={
          currentUser?.role_id == USER_ROLES.ADMIN || currentUser?.role_id == USER_ROLES.SUB_ADMIN
            ? true
            : currentUser?.subscription_status === 'active'
        }
      > */}
      <>
        <Routes>
          <Route
            element={
              <>
                <ProfileDetailHeader
                  user={userData}
                  uuid={uuid}
                  links={links}
                  isLoading={loading}
                  onManualEntrySuccess={handleManualEntrySuccess}
                />
                <Outlet />
              </>
            }
          >
            <Route
              index
              element={
                <Navigate
                  to={
                    currentUser?.role_id === USER_ROLES.DOCTOR ||
                    currentUser?.role_id === USER_ROLES.NURSE ||
                    currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT
                      ? `/patient-details/${uuid}/health-metrics`
                      : `/patient-details/${uuid}/overview`
                  }
                  replace
                />
              }
            />

            <Route
              path='health-metrics'
              element={
                <RoutesRoleGuard
                  allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.ADMIN, USER_ROLES.MEDICAL_ASSISTANT]}
                >
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Health Details</PageTitle>
                  {/* remount / refetch whenever refreshKey changes */}
                  <PatientHealthMeasurement key={refreshKey} />
                </RoutesRoleGuard>
              }
            />

            <Route
              path='patient-time-tracked'
              element={
                <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Time Tracked</PageTitle>
                  <PatientTimeTracked />
                </RoutesRoleGuard>
              }
            />

            <Route
              path='devices'
              element={
                <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Devices</PageTitle>
                  <PatientDevicesComponent patientData={userData} />
                </RoutesRoleGuard>
              }
            />

            <Route
              path='alerts-settings'
              element={
                <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Alerts Settings</PageTitle>
                  <PatientAlertsSettings />
                </RoutesRoleGuard>
              }
            />

            <Route
              path='health-alert'
              element={
                <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Health Alerts</PageTitle>
                  <PatientHealthCriticalityAlerts />
                </RoutesRoleGuard>
              }
            />

            <Route
              path='billing'
              element={
                <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Billing</PageTitle>
                  <PatientBillingReport />
                </RoutesRoleGuard>
              }
            />

            <Route
              path='overview'
              element={
                <RoutesRoleGuard
                  allowedRoles={[
                    USER_ROLES.DOCTOR,
                    USER_ROLES.NURSE,
                    USER_ROLES.ADMIN,
                    USER_ROLES.SUB_ADMIN,
                    USER_ROLES.MEDICAL_ASSISTANT,
                  ]}
                >
                  <PageTitle breadcrumbs={accountBreadCrumbs}>Overview</PageTitle>
                  <ProfileDetailOverview
                    user={userData}
                    onEditPress={handleEditPress}
                    isLoading={loading}
                  />
                </RoutesRoleGuard>
              }
            />
          </Route>

          <Route path='*' element={<Navigate to='/error/404' replace />} />
        </Routes>

        <PatientForm
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          handleSubmit={handleSubmit}
          initialData={userData}
          mode='edit'
        />
      </>
      {/* </BlurSection> */}
    </>
  )
}

export default PatientDetailsPage
